import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { pubchemAPI } from '../services/api';

function PubChemDetail() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [compound, setCompound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCompoundDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await pubchemAPI.getCompound(cid);
        if (response.data) {
          setCompound(response.data);
        } else {
          setError('Compound not found');
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    if (cid) {
      fetchCompoundDetails();
    }
  }, [cid]);

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="loading-spinner"></div>
          <p>Loading compound details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="error-message">
          <p>⚠️ Error: {error}</p>
          <button onClick={() => navigate('/pubchem')} className="btn-primary">
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  if (!compound) {
    return (
      <div className="page-container">
        <div className="error-message">
          <p>Compound not found</p>
          <button onClick={() => navigate('/pubchem')} className="btn-primary">
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <button 
        onClick={() => navigate(-1)} 
        className="back-button"
        style={{
          marginBottom: '1rem',
          padding: '0.5rem 1rem',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#e9ecef';
          e.target.style.borderColor = '#adb5bd';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#f8f9fa';
          e.target.style.borderColor = '#dee2e6';
        }}
      >
        <span>←</span> Back to Search
      </button>

      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '2rem',
        borderRadius: '8px',
        marginBottom: '2rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold' }}>
          {compound.name || compound.iupac_name || 'Unknown Compound'}
        </h1>
        {compound.iupac_name && compound.iupac_name !== compound.name && (
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.1rem', opacity: 0.95 }}>
            IUPAC: {compound.iupac_name}
          </p>
        )}
        {compound.molecular_formula && (
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.1rem', opacity: 0.95, fontFamily: 'monospace' }}>
            {compound.molecular_formula}
          </p>
        )}
      </div>

      {compound.description && (
        <div style={{
          backgroundColor: '#e7f3ff',
          border: '1px solid #b3d9ff',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '2rem',
          fontSize: '0.95rem',
          color: '#004085',
          lineHeight: '1.6'
        }}>
          <strong>Description:</strong> {compound.description}
        </div>
      )}

      <div className="detail-section">
        <h2 className="section-title" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          fontSize: '1.5rem',
          color: '#2c3e50'
        }}>
          🔬 Identifiers
        </h2>
        <div style={{ 
          backgroundColor: 'white',
          border: '1px solid #e1e8ed',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <div style={{ display: 'grid', gap: '0' }}>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: '200px 1fr',
              padding: '1rem',
              borderBottom: compound.inchi_key ? '1px solid #e1e8ed' : 'none',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
            >
              <span style={{ 
                fontWeight: '600',
                color: '#495057',
                fontSize: '0.95rem'
              }}>PubChem CID</span>
              <span style={{ 
                color: '#212529',
                fontSize: '0.95rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{
                  backgroundColor: '#667eea',
                  color: 'white',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  fontSize: '0.9rem',
                  fontWeight: 'bold'
                }}>
                  {compound.cid || 'N/A'}
                </span>
              </span>
            </div>
            
            {compound.inchi_key && (
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: '200px 1fr',
                padding: '1rem',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                <span style={{ 
                  fontWeight: '600',
                  color: '#495057',
                  fontSize: '0.95rem'
                }}>InChI Key</span>
                <span style={{ 
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  color: '#212529',
                  wordBreak: 'break-all'
                }}>
                  {compound.inchi_key}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {(compound.molecular_formula || compound.molecular_weight) && (
        <div className="detail-section">
          <h2 className="section-title" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            fontSize: '1.5rem',
            color: '#2c3e50'
          }}>
            ⚗️ Molecular Properties
          </h2>
          <div style={{ 
            backgroundColor: 'white',
            border: '1px solid #e1e8ed',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div style={{ display: 'grid', gap: '0' }}>
              {compound.molecular_formula && (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '200px 1fr',
                  padding: '1rem',
                  borderBottom: '1px solid #e1e8ed',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <span style={{ fontWeight: '600', color: '#495057', fontSize: '0.95rem' }}>Formula</span>
                  <span style={{ fontFamily: 'monospace', fontSize: '1.1rem', color: '#212529' }}>
                    {compound.molecular_formula}
                  </span>
                </div>
              )}
              {compound.molecular_weight && (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '200px 1fr',
                  padding: '1rem',
                  borderBottom: (compound.xlogp !== undefined || compound.tpsa || compound.complexity || compound.charge) ? '1px solid #e1e8ed' : 'none',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <span style={{ fontWeight: '600', color: '#495057', fontSize: '0.95rem' }}>Molecular Weight</span>
                  <span style={{ color: '#212529', fontSize: '0.95rem' }}>
                    {parseFloat(compound.molecular_weight).toFixed(2)} g/mol
                  </span>
                </div>
              )}
              {compound.xlogp !== undefined && compound.xlogp !== null && (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '200px 1fr',
                  padding: '1rem',
                  borderBottom: (compound.tpsa || compound.complexity || compound.charge) ? '1px solid #e1e8ed' : 'none',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <span style={{ fontWeight: '600', color: '#495057', fontSize: '0.95rem' }}>XLogP (Lipophilicity)</span>
                  <span style={{ color: '#212529', fontSize: '0.95rem' }}>{compound.xlogp}</span>
                </div>
              )}
              {compound.tpsa && (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '200px 1fr',
                  padding: '1rem',
                  borderBottom: (compound.complexity || compound.charge) ? '1px solid #e1e8ed' : 'none',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <span style={{ fontWeight: '600', color: '#495057', fontSize: '0.95rem' }}>TPSA</span>
                  <span style={{ color: '#212529', fontSize: '0.95rem' }}>{compound.tpsa} Ų</span>
                </div>
              )}
              {compound.complexity && (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '200px 1fr',
                  padding: '1rem',
                  borderBottom: compound.charge ? '1px solid #e1e8ed' : 'none',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <span style={{ fontWeight: '600', color: '#495057', fontSize: '0.95rem' }}>Complexity</span>
                  <span style={{ color: '#212529', fontSize: '0.95rem' }}>{parseFloat(compound.complexity).toFixed(1)}</span>
                </div>
              )}
              {compound.charge !== 0 && compound.charge !== undefined && (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '200px 1fr',
                  padding: '1rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <span style={{ fontWeight: '600', color: '#495057', fontSize: '0.95rem' }}>Formal Charge</span>
                  <span style={{ color: '#212529', fontSize: '0.95rem' }}>
                    {compound.charge > 0 ? '+' : ''}{compound.charge}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {(compound.h_bond_donor_count !== undefined || compound.h_bond_acceptor_count !== undefined) && (
        <div className="detail-section">
          <h2 className="section-title" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            fontSize: '1.5rem',
            color: '#2c3e50'
          }}>
            🔗 Hydrogen Bonding & Flexibility
          </h2>
          <div style={{ 
            backgroundColor: 'white',
            border: '1px solid #e1e8ed',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div style={{ display: 'grid', gap: '0' }}>
              {compound.h_bond_donor_count !== undefined && (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '200px 1fr',
                  padding: '1rem',
                  borderBottom: (compound.h_bond_acceptor_count !== undefined || compound.rotatable_bond_count !== undefined) ? '1px solid #e1e8ed' : 'none',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <span style={{ fontWeight: '600', color: '#495057', fontSize: '0.95rem' }}>H-Bond Donors</span>
                  <span style={{ color: '#212529', fontSize: '0.95rem' }}>{compound.h_bond_donor_count}</span>
                </div>
              )}
              {compound.h_bond_acceptor_count !== undefined && (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '200px 1fr',
                  padding: '1rem',
                  borderBottom: compound.rotatable_bond_count !== undefined ? '1px solid #e1e8ed' : 'none',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <span style={{ fontWeight: '600', color: '#495057', fontSize: '0.95rem' }}>H-Bond Acceptors</span>
                  <span style={{ color: '#212529', fontSize: '0.95rem' }}>{compound.h_bond_acceptor_count}</span>
                </div>
              )}
              {compound.rotatable_bond_count !== undefined && (
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '200px 1fr',
                  padding: '1rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <span style={{ fontWeight: '600', color: '#495057', fontSize: '0.95rem' }}>Rotatable Bonds</span>
                  <span style={{ color: '#212529', fontSize: '0.95rem' }}>{compound.rotatable_bond_count}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {compound.synonyms && compound.synonyms.length > 0 && (
        <div className="detail-section">
          <h2 className="section-title" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            fontSize: '1.5rem',
            color: '#2c3e50'
          }}>
            📝 Synonyms & Alternative Names
          </h2>
          <div style={{ 
            backgroundColor: 'white',
            border: '1px solid #e1e8ed',
            borderRadius: '8px',
            padding: '1.5rem'
          }}>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '0.5rem' 
            }}>
              {compound.synonyms.map((synonym, index) => (
                <span 
                  key={index}
                  style={{
                    backgroundColor: '#f0f4ff',
                    color: '#667eea',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    border: '1px solid #d0d9ff'
                  }}
                >
                  {synonym}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {compound.canonical_smiles && (
        <div className="detail-section">
          <h2 className="section-title" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            fontSize: '1.5rem',
            color: '#2c3e50'
          }}>
            🧬 Chemical Structure</h2>
          <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>Canonical SMILES:</strong>
            </p>
            <p style={{ 
              fontFamily: 'monospace', 
              fontSize: '0.9rem',
              backgroundColor: 'white',
              padding: '1rem',
              borderRadius: '4px',
              wordBreak: 'break-all',
              overflowWrap: 'break-word'
            }}>
              {compound.canonical_smiles}
            </p>
            {compound.isomeric_smiles && compound.isomeric_smiles !== compound.canonical_smiles && (
              <>
                <p style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
                  <strong>Isomeric SMILES:</strong>
                </p>
                <p style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '0.9rem',
                  backgroundColor: 'white',
                  padding: '1rem',
                  borderRadius: '4px',
                  wordBreak: 'break-all',
                  overflowWrap: 'break-word'
                }}>
                  {compound.isomeric_smiles}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <div className="detail-section">
        <h2 className="section-title" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          fontSize: '1.5rem',
          color: '#2c3e50'
        }}>
          🔗 Source Information
        </h2>
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #e1e8ed',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <div style={{ 
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <div>
              <div style={{ 
                fontWeight: '600',
                color: '#495057',
                fontSize: '0.95rem',
                marginBottom: '0.5rem'
              }}>
                Official PubChem Database
              </div>
              <div style={{ 
                fontSize: '0.85rem',
                color: '#6c757d'
              }}>
                Access complete compound information and bioactivity data
              </div>
            </div>
            <a 
              href={`https://pubchem.ncbi.nlm.nih.gov/compound/${compound.cid}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                backgroundColor: '#667eea',
                color: 'white',
                textDecoration: 'none',
                fontWeight: '600',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s ease',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.95rem',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#5568d3';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#667eea';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
            >
              <span>View on PubChem</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
            </a>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '2rem', textAlign: 'center' }}>
        <button 
          onClick={() => navigate(-1)} 
          className="btn-secondary"
          style={{
            padding: '0.75rem 2rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '1rem',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#5a6268'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
        >
          ← Back to Search Results
        </button>
      </div>
    </div>
  );
}

export default PubChemDetail;
